import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Brain, ArrowRight, Info } from 'lucide-react';
import { api, DOMAIN_NAMES, getScoreLevel } from '../lib/api';
import MindWheel from '../components/visualizations/MindWheel';
import SystemPyramid from '../components/visualizations/SystemPyramid';
import NeuralFrictionMap from '../components/visualizations/NeuralFrictionMap';
import BeliefArchitectureMap from '../components/visualizations/BeliefArchitectureMap';

export default function MindMap() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await api.get('/reports/latest');
        setReport(response.data);
      } catch (error) {
        console.error('Error fetching report:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading mind map...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <Brain className="h-16 w-16 text-muted-foreground/50 mb-4" strokeWidth={1} />
        <h2 className="text-xl font-semibold mb-2">No Mind Map Available</h2>
        <p className="text-muted-foreground mb-6">Complete an assessment to generate your mind architecture visualization.</p>
        <Link to="/assessment/full">
          <Button className="glow-primary" data-testid="start-assessment">
            Start Diagnostic
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  const pattern = report.belief_architecture_pattern || report.cluster_profile;

  return (
    <div className="space-y-6" data-testid="mind-map-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Mind Architecture Map</h1>
          <p className="text-muted-foreground">Visual representation of your mental operating system</p>
        </div>
        <Link to="/assessment/full">
          <Button variant="outline" data-testid="retake-assessment">
            Retake Assessment
          </Button>
        </Link>
      </div>

      {/* Color Legend */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-6 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">Legend:</span>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-emerald-500" />
              <span className="text-xs">Balanced (0-25)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-amber-500" />
              <span className="text-xs">Mild Friction (25-50)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-orange-500" />
              <span className="text-xs">Moderate (50-75)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-500" />
              <span className="text-xs">High Friction (75-100)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Visualizations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Mind Architecture Wheel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border/50 h-full">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                Mind Architecture Wheel
                <Info className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Radial view of neural friction levels across 7 domains
              </p>
            </CardHeader>
            <CardContent>
              <MindWheel domainScores={report.domain_scores} size="large" />
            </CardContent>
          </Card>
        </motion.div>

        {/* System Pyramid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card border-border/50 h-full">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                System Hierarchy Pyramid
                <Info className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Dependency layers - higher systems depend on lower stability
              </p>
            </CardHeader>
            <CardContent>
              <SystemPyramid domainScores={report.domain_scores} />
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Neural Friction Map (Renamed from Mental Friction Heatmap) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              Neural Friction Map
              <Info className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Highlights areas of stress processing, cognitive overload, emotional reactivity, and decision fatigue
            </p>
          </CardHeader>
          <CardContent>
            <NeuralFrictionMap domainScores={report.domain_scores} />
          </CardContent>
        </Card>
      </motion.div>

      {/* Belief Architecture Map (NEW) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              Belief Architecture Map
              <Info className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Shows how beliefs shape behavior through interpretation and emotional response layers
            </p>
          </CardHeader>
          <CardContent>
            <BeliefArchitectureMap beliefPattern={pattern} domainScores={report.domain_scores} />
          </CardContent>
        </Card>
      </motion.div>

      {/* Domain Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Domain Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {report.domain_scores.map((domain, index) => {
                const level = getScoreLevel(domain.dysfunction_score);
                return (
                  <div 
                    key={domain.domain}
                    className="p-4 rounded-lg bg-secondary/30 border border-border/50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-mono text-muted-foreground">0{index + 1}</span>
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${level.color}20`, color: level.color }}
                      >
                        {level.label}
                      </span>
                    </div>
                    <h4 className="font-medium text-sm mb-2">{DOMAIN_NAMES[domain.domain]}</h4>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Neural Friction</p>
                        <p className="text-2xl font-mono font-bold" style={{ color: level.color }}>
                          {domain.dysfunction_score.toFixed(0)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Stability</p>
                        <p className="text-lg font-mono text-emerald-500">
                          {domain.strength_score.toFixed(0)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
